import type { Price, WatchlistItem } from './stockTypes';

/**
 * 관심종목 한 줄에 무엇을 보여줄지 정한다. (16.5)
 *
 * 화면 컴포넌트에서 빼낸 이유
 *   "실시간 시세와 마지막 종가 중 무엇을 쓸 것인가" 는 규칙이지 표시가 아니다.
 *   컴포넌트 안에 두면 렌더링 테스트 없이는 검증할 수 없는데,
 *   등락 부호가 반대로 나오는 종류의 버그는 눈으로 잘 안 잡힌다.
 */

/**
 * 일봉 두 개로 등락률을 계산한다.
 *
 * 시세를 못 받았을 때 쓰는 대체값이다. 목록 API 가 마지막 두 봉의 종가를 주므로
 * "직전 영업일 대비 마지막 영업일" 을 알 수 있다.
 *
 * @returns 기준이 없거나 0 이면 null. 0 으로 채우면 화면에 +100% 로 보인다
 */
export function fallbackChangeRate(
  lastClose: number | null,
  prevClose: number | null,
): number | null {
  if (lastClose === null || prevClose === null || prevClose === 0) {
    return null;
  }
  return ((lastClose - prevClose) / prevClose) * 100;
}

/**
 * 한 줄에 표시할 가격과 등락, 그리고 그것이 실시간인지.
 *
 * 🔴 두 종류의 가격이 섞이지 않게 한다
 *   목록 API 의 lastClose 는 마지막 일봉의 종가이고,
 *   시세 API 의 lastPrice 는 지금 값이다. 장중에는 다르다.
 *   같은 모양으로 보여주면 주말에 본 값을 실시간으로 착각한다.
 */
export function resolveDisplayPrice(
  item: WatchlistItem,
  price: Price | undefined,
): { value: number | null; changeRate: number | null; live: boolean } {
  if (price !== undefined) {
    return {
      value: price.lastPrice,
      // 시세 응답에 등락이 없으면(기준 봉이 없는 신규 상장주) 일봉으로도 시도한다.
      changeRate: price.changeRate ?? fallbackChangeRate(item.lastClose, item.prevClose),
      live: !price.stale,
    };
  }

  return {
    value: item.lastClose,
    changeRate: fallbackChangeRate(item.lastClose, item.prevClose),
    live: false,
  };
}

/**
 * 시세가 언제 기준인지 알려줄 문구. (2026-08-19)
 *
 * 🔴 왜 필요한가
 *   화면 숫자가 몇십 초 전 값일 수 있는데 그것이 드러나지 않았다.
 *   "실시간" 이라고만 적혀 있으면 지금 값으로 읽는다.
 *
 *   갱신 경로가 3단이라 나이가 쌓인다. (2026-09-16 에 15초로 당김. 처음엔 매분 → 90초 → 30초)
 *     백엔드 배치 15초 → Redis 캐시 장중 25초 → 프론트 폴링 15초
 *   최악 30초 남짓, 보통 15초 안팎 된 값이다.
 *
 * 🔴 가장 최근 quotedAt 을 쓴다
 *   종목마다 조금씩 다를 수 있다. 배치가 한 번에 갱신하므로 몇 초 차이지만,
 *   거래정지 종목은 옛 값이 남아 훨씬 오래됐을 수 있다.
 *   그 하나 때문에 전체를 옛날 시각으로 적으면 멀쩡한 종목까지 낡아 보인다.
 *   "언제 마지막으로 값을 받았나" 를 묻는 것이므로 최신이 맞다.
 *
 * @return "09:47:12" 형태. 시각을 하나도 못 읽으면 null
 */
export function quoteBasisTime(prices: Price[] | undefined): string | null {
  if (!prices || prices.length === 0) {
    return null;
  }

  let latest: Date | null = null;
  for (const price of prices) {
    if (!price.quotedAt) {
      continue;
    }
    const at = new Date(price.quotedAt);
    if (Number.isNaN(at.getTime())) {
      continue;
    }
    if (latest === null || at.getTime() > latest.getTime()) {
      latest = at;
    }
  }

  if (latest === null) {
    return null;
  }
  return [latest.getHours(), latest.getMinutes(), latest.getSeconds()]
    .map((v) => String(v).padStart(2, '0'))
    .join(':');
}


/**
 * 미국 마켓인가. 백엔드 StockUniversePolicy.US_MARKETS 와 같은 목록이다. (2026-08-20)
 *
 * 통화 표기(원/달러)와 국내/해외 탭 분류가 이것 하나를 본다.
 * market 이 null 이면 국내로 본다 - 기존 데이터가 전부 국내였다.
 */
const US_MARKETS = new Set(['NYSE', 'NASDAQ', 'AMEX', 'US_ETC']);

export function isUsMarket(market: string | null | undefined): boolean {
  return market != null && US_MARKETS.has(market);
}

/**
 * 시세 한 줄에 그릴 값. (2026-09-02 사용자 요청 - 증권사 관심종목 화면처럼)
 *
 * 🔴 봉(캔들)은 넣지 않는다
 *   토스 현재가 API 가 주는 것은 심볼·시각·현재가·통화 넷뿐이다.
 *   시가·고가·저가가 없어서 당일 봉을 그릴 수 없다. (TossPrice 주석)
 *   일봉으로 그리면 장중 내내 어제 봉이 떠 있어 지금 흐름으로 오해된다.
 *
 * 🔴 시세가 없으면 마지막 종가로 대신하되 그 사실을 표시한다
 *   장 시작 전·주말·거래정지에서 실제로 일어난다. 같은 모양으로 보여주면
 *   주말에 본 값을 실시간으로 착각한다.
 *
 * @param price   시세 API 응답. 아직 못 받았으면 undefined
 * @param close   마지막 일봉 종가. 시세가 없을 때 대신 쓴다
 * @param prev    그 직전 일봉 종가. 시세가 없을 때 등락을 여기서 계산한다
 */
export interface QuoteView {
  /** 화면에 찍을 가격 */
  price: number | null;
  /** 전일 대비. 부호를 그대로 갖는다 (음수면 하락) */
  change: number | null;
  changeRate: number | null;
  /** 화살표와 색을 정한다 */
  direction: 'up' | 'down' | 'flat';
  /** 장 마감이거나 시세를 못 받았다. 색을 죽인다 */
  stale: boolean;
  /** 거래소. "KOSPI" "NASDAQ" 등. 모르면 null */
  market: string | null;
}

export function resolveQuote(
  price: Price | undefined,
  close: number | null,
  prev: number | null,
): QuoteView {
  const live = price !== undefined;
  const value = live ? price.lastPrice : close;

  // 시세 응답에 등락이 없으면(기준 봉이 없는 신규 상장주) 일봉으로도 시도한다
  const rate = live
    ? (price.changeRate ?? fallbackChangeRate(price.lastPrice, prev))
    : fallbackChangeRate(close, prev);

  const diff = live
    ? (price.change ?? (value !== null && prev !== null ? value - prev : null))
    : close !== null && prev !== null
      ? close - prev
      : null;

  return {
    price: value,
    change: diff,
    changeRate: rate,
    direction: diff === null || diff === 0 ? 'flat' : diff > 0 ? 'up' : 'down',
    // 시세를 아예 못 받은 것도 stale 이다 - 마지막 종가를 보고 있다는 뜻이다
    stale: !live || price.stale,
    market: live ? price.market : null,
  };
}

/**
 * 시세 글자색. 한국 시장 관행대로 상승이 빨강, 하락이 파랑이다.
 *
 * 🔴 장이 닫혀도 색을 유지한다 (2026-09-03 사용자 요청 — 전날 결정을 뒤집음)
 *
 *   2026-09-02 에는 마감이면 회색으로 죽였다. "마감가에 빨강·파랑이 남아 있으면
 *   지금 움직이는 값으로 읽힌다" 는 이유였다.
 *
 *   실제로 써 보니 **화면을 보는 시간의 대부분이 장 마감**이라 목록이 통째로
 *   회색이 됐다. 오른 종목과 내린 종목이 같은 색이라 한눈에 구분이 안 된다.
 *   증권사 앱들도 마감 후 색을 유지한다 - 색은 "지금 움직인다" 가 아니라
 *   "전일 대비 어느 쪽인가" 를 말하는 것이기 때문이다.
 *
 *   장 마감이라는 사실은 목록 머리("장 마감")가 한 번만 말한다.
 *   줄마다 반복하면 정작 중요한 종목명이 밀린다.
 */
export function quoteColorClass(view: QuoteView): string {
  if (view.direction === 'flat') {
    return 'text-fg-muted';
  }
  return view.direction === 'up' ? 'text-rise' : 'text-fall';
}

/** 대비 앞에 붙는 기호. 마감이면 방향만 남기고 색은 죽는다 */
export function quoteArrow(view: QuoteView): string {
  if (view.direction === 'up') return '▲';
  if (view.direction === 'down') return '▼';
  return '−';
}

/**
 * 거래소 표기. 화면에 그대로 찍는다.
 *
 * 우리 DB 의 market 값은 토스가 준 코드다(KOSPI · KOSDAQ · NASDAQ · NYSE · AMEX).
 * 나스닥·뉴욕은 한국어 관행 표기로 바꾸고 나머지는 코드를 그대로 쓴다 -
 * 지어낸 이름을 붙이면 어느 거래소인지 오히려 흐려진다.
 */
const MARKET_LABEL: Record<string, string> = {
  NASDAQ: '나스닥',
  NYSE: '뉴욕',
  AMEX: '아멕스',
};

export function marketLabel(market: string | null | undefined): string | null {
  if (market == null || market === '' || market === 'US_ETC') {
    return null;
  }
  return MARKET_LABEL[market] ?? market;
}
